
import { Component, Mixins } from "vue-property-decorator";
import VueSelect from 'vue-select';
import SideMenuLayout from "@/views/sidebar/side-menu-layout.vue";
import WatchStatisticsMixin from "@/views/sidebar/watch-statistics/watch-statistics.mixin";
import BaseMixin from "@/views/base/base.mixin";
import moment from "moment";

type SideMenuLayoutInstance = InstanceType<typeof SideMenuLayout>;
type VueSelectInstance = InstanceType<typeof VueSelect>;

@Component({
  components: {
    "side-menu-layout": SideMenuLayout
  }
})
export default class WatchStatisticsComponent extends Mixins(WatchStatisticsMixin, BaseMixin) {
  public watchId: string | null = null;
  alertVisible = false;
  loading = false;
  showAvatar = false;

  get displayDate(): string {
    return moment(this.pickDate).format("MMMM Do, YYYY");
  }

  get watchImei(): string {
    return this.selectedPatientSummary.imeiId ?? "Not assigned a watch.";
  }

  get watchVersion(): string {
    return this.selectedPatientSummary.appVersion ?? "n/a";
  }

  async mounted(): Promise<void> {
    this.loading = true;
    this.showAvatar = this.$sidebar.state?.item.showAvatar ?? false;
    const client = await this.waitForClientReady();
    if (client) {
      await this.fetchData();
    } else {
      console.warn("Client not ready in time.");
    }
    this.loading = false;
  }

  async fetchData(): Promise<void> {
    const date = moment(this.pickDate).format("YYYY/MM/DD");

    const fetch = this.selectedPatientSummary.watch ? [
      this.$store.dispatch("WatchStatistics/fetchWatchWorn", {
        date: date, 
        clientId: this.selectedClientDDL.value,
        participantId: this.selectedPatientSummary.participantId,
        timezone: this.selectedClient.timezone
      }),
      this.$store.dispatch("WatchStatistics/fetchWatchCharging", {
        date: date, 
        clientId: this.selectedClientDDL.value,
        participantId: this.selectedPatientSummary.participantId,
        timezone: this.selectedClient.timezone
      }),
      this.$store.dispatch("WatchStatistics/fetchBiometricRecordCount", {
        date: date, 
        clientId: this.selectedClientDDL.value,
        participantId: this.selectedPatientSummary.participantId,
        timezone: this.selectedClient.timezone
      }),
    ] : [
      await this.$store.dispatch("WatchStatistics/fetchActiveImeis", this.selectedClientDDL.value),
    ];
    await Promise.all(fetch);
  }

  async moveTime(forward: boolean): Promise<void> {
    this.loading = true;
    this.pickDate = moment(this.pickDate)
      [forward ? "add" : "subtract"](1, "days")
      .toDate();
    await this.fetchData();
    this.loading = false;
  }

  getWornCellStyle(value: number) {
    return {
      backgroundColor: value ? '#629A34' : '#D9D9D9',
      width: '8%',
      margin: '1px',
    };
  }

  getExportCellStyle(value: number, index: number, isAm: boolean) {
    const isWorn = isAm ? this.watchWorn.am[index] : this.watchWorn.pm[index];
    return {
      backgroundColor: value > 0 && value < 5 ? 
        '#efbe5e' : value == 0 && isWorn ? '#f5231f' : '#D9D9D9',
      width: '8%',
      margin: '1px',
    };
  }

  get timeWornHours() {
    const count = [...this.watchWorn.am, ...this.watchWorn.pm]
      .filter(value => value === 1)
      .length;

    const label = count === 1 ? 'hour' : 'hours';
    return `Time Worn: Approximately ${count} ${label}`;
  }

  get watchChargingActive() {
    const count = [...this.watchCharging.am, ...this.watchCharging.pm]
      .filter(item => item.active)
      .length;

    const label = count === 1 ? 'hour' : 'hours';
    return `Charge Time: Approximately ${count} ${label}`;
  }

  get biometricCount() {
    const count = [...this.biometricRecordCount.am, ...this.biometricRecordCount.pm]
      .filter(item => item >= 5)
      .length;
    const percentage = ((count / 24) * 100).toFixed(1);

    return `Data Export: Approximately ${percentage}%`;
  }

  get watchAssignmentInstructions(): string {
    return "To assign a watch to this client, click the Watch ID dropdown and select the appropriate device from the list. Once selected, the watch will be linked to that participant.";
  }

  handleBlur(): void {
    const input = (this.$refs.watchSelect as VueSelectInstance)?.$el?.querySelector('input');
    input?.blur();
  }

  async handleWatchAssignment(): Promise<void> {
    this.$validator.validateAll().then(async (isValid: boolean) => {
      if (isValid) {
        this.loading = true;
        await this.$store.dispatch("WatchStatistics/upsertParticipantIMEIMap", {
          clientId: this.selectedClientDDL.value,
          participantId: this.selectedPatientSummary.participantId,
          imeiId: this.selectedPatientSummary.imeiId ?? this.watchId,
          changedByUserId: null,
          assign:  !this.selectedPatientSummary.watch
        });
        this.loading = false;
        if (!this.errorMessage) {
          this.$sidebar.close( {success: true, reload: true} );
        } else {
          (this.$refs.sideMenu as SideMenuLayoutInstance).showAlert();
        }
      }
    });
  }
}
